import {
  faBracketsCurly,
  faChevronDown,
  faSearch,
  faText,
} from "@fortawesome/pro-regular-svg-icons";
import {
  Content,
  Header,
  Item,
  Root,
  Trigger,
} from "@radix-ui/react-accordion";
import { useState, useMemo } from "react";

import { Icon } from "src/base-components/Icon";
import { Input } from "src/base-components/Input";
import { Pill } from "src/base-components/Pill";
import { Tooltip } from "src/design-system/Tooltip";
import {
  CaseSchemaDisplay,
  JsonSchemaProperty,
  ParsedSchemaSection,
} from "src/schema/schemaEditor/caseSchemaUtils";
import { TYPE_ICONS } from "src/utils/constants";

interface CaseSchemaHintProps {
  selectedSchema: CaseSchemaDisplay;
  rootPath: string;
}

const CaseSchemaPathPill = ({ path }: { path: string }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip placement="top" title={copied ? "Copied!" : path} asChild>
      <Pill
        size="sm"
        variant="indigo-light"
        maxWidth
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(path);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}
      >
        <Pill.Icon icon={faBracketsCurly} />
        <Pill.Text>{path}</Pill.Text>
      </Pill>
    </Tooltip>
  );
};

const PropertyPill = ({
  property,
  schema,
}: {
  property: string;
  schema: JsonSchemaProperty;
}) => {
  const typeIcon = TYPE_ICONS[schema.type as keyof typeof TYPE_ICONS] || faText;
  return (
    <Pill size="sm" variant="gray">
      <Pill.Icon color="text-gray-400" icon={typeIcon} size="sm" />
      <Pill.Text fontType="code">{property}</Pill.Text>
    </Pill>
  );
};

const PropertyContainer: React.FC<{
  properties: [string, JsonSchemaProperty][];
}> = ({ properties }) => (
  <div className="mb-5 ml-3.5 flex max-w-full flex-row flex-wrap gap-x-1 gap-y-1">
    {properties.map(([key, schema]) => (
      <PropertyPill key={key} property={key} schema={schema} />
    ))}
  </div>
);

const SchemaSection: React.FC<{
  section: ParsedSchemaSection;
  searchTerm: string;
}> = ({ section, searchTerm }) => {
  const matchesSearch = (text: string) =>
    text.toLowerCase().includes(searchTerm.toLowerCase());

  const filteredProperties = searchTerm
    ? Object.entries(section.properties).filter(
        ([key]) => matchesSearch(key) || matchesSearch(section.name),
      )
    : Object.entries(section.properties);

  if (
    searchTerm &&
    filteredProperties.length === 0 &&
    !matchesSearch(section.name)
  ) {
    return null;
  }

  return (
    <div className="items-top flex w-full">
      <div className="mt-2.5 h-[1px] w-3.5 flex-shrink-0 bg-gray-200" />
      <div className="min-w-0 flex-1">
        <Item
          className="w-full max-w-full"
          value={`case-section-${section.id}`}
        >
          <Header className="w-full max-w-full">
            <Trigger className="group w-full max-w-full">
              <div className="ml-0.5 flex flex-row items-center gap-x-2 overflow-hidden text-left">
                <div className="group-radix-state-closed:-rotate-90 group-radix-state-closed:duration-300 group-radix-state-open:duration-300">
                  <Icon color="text-gray-500" icon={faChevronDown} size="sm" />
                </div>
                <div className="flex-1 text-gray-800 font-inter-semibold-13px">
                  {section.name}
                </div>
                <div className="min-w-0 flex-shrink">
                  <CaseSchemaPathPill path={section.path} />
                </div>
              </div>
            </Trigger>
          </Header>
          <Content className="relative pl-3">
            <div className="absolute bottom-[14px] left-3 top-1 w-[1px] border-l border-gray-200" />
            <div className="mt-2">
              <PropertyContainer properties={filteredProperties} />
            </div>
          </Content>
        </Item>
      </div>
    </div>
  );
};

export const CaseSchemaHint: React.FC<CaseSchemaHintProps> = ({
  selectedSchema,
  rootPath,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredData = useMemo(() => {
    if (!searchTerm) {
      return {
        primitiveProperties: Object.entries(selectedSchema.primitiveProperties),
        nestedSections: selectedSchema.nestedSections,
      };
    }

    const matchesSearch = (text: string) =>
      text.toLowerCase().includes(searchTerm.toLowerCase());

    const filteredPrimitiveProperties = Object.entries(
      selectedSchema.primitiveProperties,
    ).filter(([key]) => matchesSearch(key));

    const filteredNestedSections = selectedSchema.nestedSections.filter(
      (section) => {
        const hasMatchingProperty = Object.keys(section.properties).some(
          (key) => matchesSearch(key),
        );
        return matchesSearch(section.name) || hasMatchingProperty;
      },
    );

    return {
      primitiveProperties: filteredPrimitiveProperties,
      nestedSections: filteredNestedSections,
    };
  }, [selectedSchema, searchTerm]);

  return (
    <div className="rounded-md border border-gray-100 bg-gray-50 px-4 py-4">
      <div className="mb-4">
        <Input
          data-loc="case-schema-hint-search"
          placeholder="Search a property..."
          prefixIcon={{ icon: faSearch }}
          value={searchTerm}
          fullWidth
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <Root
        data-loc="case-schema-hint"
        defaultValue={["case-main"]}
        type="multiple"
      >
        <Item className="w-full max-w-full" value="case-main">
          <Header className="w-full max-w-full">
            <Trigger className="group w-full max-w-full">
              <div className="ml-0.5 flex flex-row items-center gap-x-2 overflow-hidden text-left">
                <div className="group-radix-state-closed:-rotate-90 group-radix-state-closed:duration-300 group-radix-state-open:duration-300">
                  <Icon color="text-gray-500" icon={faChevronDown} size="sm" />
                </div>
                <div className="flex flex-1 items-center gap-x-2 text-gray-800 font-inter-semibold-13px">
                  Case
                  <Pill
                    dataLoc="case-schema-hint-pill"
                    size="sm"
                    variant="gray"
                  >
                    <Pill.Icon
                      color="text-gray-400"
                      icon={selectedSchema.icon}
                    />
                    <Pill.Text>{selectedSchema.name}</Pill.Text>
                  </Pill>
                </div>
                <div className="min-w-0 flex-shrink">
                  <CaseSchemaPathPill path={rootPath} />
                </div>
              </div>
            </Trigger>
          </Header>
          <Content className="relative pl-3">
            <div className="absolute bottom-[14px] left-3 top-1 w-[1px] border-l border-gray-200" />
            <div className="mt-2">
              {filteredData.primitiveProperties.length > 0 && (
                <PropertyContainer
                  properties={filteredData.primitiveProperties}
                />
              )}

              {filteredData.nestedSections.map((section) => (
                <SchemaSection
                  key={section.id}
                  searchTerm={searchTerm}
                  section={section}
                />
              ))}
            </div>
          </Content>
        </Item>
      </Root>
    </div>
  );
};
